/* Presentation rules for Auto Suggest on a dataset's Rules tab. Pure — no React, no fetch.
 *
 * **Recommend, select, bind, run — four steps a person takes, never one the platform takes alone.**
 * Only a `bind` row is selectable, because only there does an approved rule exist; a row that needs
 * a new rule goes to the Suggestions screen, where a person drafts it and two others approve it.
 *
 * **Strong evidence arrives ticked and a guess does not**, which is the server's `preselected` rather
 * than a judgement made here: the screen and the API cannot then disagree about what a default
 * selection means.
 */

import { SCREEN_PATHS } from '@/shared/routes';

import type {
  AutoSuggestPreview,
  AutoSuggestRecommendation,
  AutoSuggestResult,
  RecommendedAction,
} from '../api/rulegen';

export interface PreviewSections {
  /** Selectable: an approved rule fits, so binding it needs no new approval. */
  readonly bindable: readonly AutoSuggestRecommendation[];
  /** No approved rule fits. Drafted in Suggestions, never here. */
  readonly needsNewRule: readonly AutoSuggestRecommendation[];
  readonly awaitingApproval: readonly AutoSuggestRecommendation[];
  readonly alreadyBound: readonly AutoSuggestRecommendation[];
}

/** Which section each action lands in. Total over the wire's enum, so a fifth action fails here. */
const SECTION_OF = {
  bind: 'bindable',
  needs_new_rule: 'needsNewRule',
  awaiting_approval: 'awaitingApproval',
  already_bound: 'alreadyBound',
} as const satisfies Record<RecommendedAction, keyof PreviewSections>;

export function sectionsOf(preview: AutoSuggestPreview): PreviewSections {
  const sections: Record<keyof PreviewSections, AutoSuggestRecommendation[]> = {
    bindable: [],
    needsNewRule: [],
    awaitingApproval: [],
    alreadyBound: [],
  };
  for (const item of preview.recommendations) {
    sections[SECTION_OF[item.action]].push(item);
  }
  return sections;
}

/** The ids ticked when the list first appears: bindable rows on strong evidence. */
export function initialSelection(preview: AutoSuggestPreview): ReadonlySet<string> {
  return new Set(
    preview.recommendations
      .filter((item) => item.action === 'bind' && item.preselected)
      .map((item) => item.suggestion_id),
  );
}

export function toggled(selection: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(selection);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** "Select all" means every *selectable* row — a row with no approved rule cannot be bound. */
export function allSelected(
  bindable: readonly AutoSuggestRecommendation[],
  selection: ReadonlySet<string>,
): boolean {
  return (
    bindable.length > 0 && bindable.every((item) => selection.has(item.suggestion_id))
  );
}

/** What the row calls the rule: the approved rule it would bind, else the name a draft would get. */
export function ruleNameOf(item: AutoSuggestRecommendation): string {
  return item.rule_name ?? item.proposed_name;
}

/** The bindings a Run should execute: every selection that is now bound here, once each.
 *
 * Several recommendations served by one any-column rule share one binding, so the ids are
 * deduplicated — a run naming one binding twice is the duplicate execution the button exists to
 * prevent. */
export function boundBindingIds(result: AutoSuggestResult): readonly string[] {
  const ids = result.results
    .filter((item) => item.outcome === 'bound' || item.outcome === 'already_bound')
    .map((item) => item.binding_id)
    .filter((id): id is string => id !== null);
  return [...new Set(ids)];
}

export type BandTone = 'ok' | 'info' | 'warn' | 'bad';

/** The bind result's tone. **A selection that could not be bound turns it amber**, because a green
 * band reads as "everything I ticked is in place". */
export function resultTone(result: AutoSuggestResult): BandTone {
  const { summary } = result;
  if (summary.failed > 0 && summary.failed === summary.selected) return 'bad';
  if (summary.failed > 0 || summary.not_bindable > 0) return 'warn';
  if (summary.bound > 0) return 'ok';
  return 'info';
}

/** Where a recommendation that needs a new rule is drafted: the Suggestions screen, on this
 * dataset, with that recommendation's apply panel open on "Create a new rule". The connection is in
 * the link because the screen's own steps are connection then dataset. */
export function draftLink(target: {
  readonly connectionId: string;
  readonly datasetId: string;
  readonly suggestionId: string;
}): string {
  const query = new URLSearchParams({
    connection: target.connectionId,
    dataset: target.datasetId,
    suggestion: target.suggestionId,
  });
  return `${SCREEN_PATHS.suggestions}?${query.toString()}`;
}
